import {useEffect} from 'react'
import * as Sentry from '@sentry/react'
import {Col, Container, Row} from 'react-bootstrap'
import {BeatLoader} from 'react-spinners'
import {HughesnetLogo} from '../CustomerForm/HughesnetLogo'
import {Header} from '../Header'
import {Order} from '../Order'
import {useDispatch, useSelector} from 'react-redux'
import {AlertPopup} from '../Utilities/GeneralComponents'
import {getStringTranslations, login, setEmbeddedPhone, setShowLoginForm} from '../../state/settingsSlice'
import {installConsumerChat} from '../../functions'
import {CaseSelector, ErrorPage, LoginForm} from './Common'
import {
    getProfileInfo, handleActiveCall,
    handleCallClear,
    handleDisposition,
    handleEndCallTask,
    handleNewCall,
} from '../../state/embeddedSlice'
import {CustomerForm} from '../CustomerForm'
import {Notes} from '../Notes'
import {WebPhone} from '../Alvaria/WebPhone'
import { setChatDisplayNumberByCallId } from '../../state/chatInboxSlice'
import { dismissChatByCallId } from '../../state/chatInboxSlice'
import store from '../../state/store'

async function checkLogin(dispatch) {
    if (window.localStorage.creds) {
        const creds = JSON.parse(window.localStorage.creds)
        const ret = await dispatch(login({...creds}))
        const [ok] = ret.payload
        if (ok) {
            Sentry.setUser({email: creds.username})
            const ret = await dispatch(getProfileInfo())
            console.log(ret)
            if (!ret.payload) {
                dispatch(setShowLoginForm(true))
                return false
            }
            return ok
        }
    }
    dispatch(setShowLoginForm(true))
    return false
}

let appLoaded = false


export const AppForEmbeddedPhone = (props) => {
    const appLoadingState = useSelector(state => state.settings.appLoadingState)
    const showLoginForm = useSelector(state => state.settings.showLoginForm)
    const existingCases = useSelector(state => state.customer.cases)
    const greeting = useSelector(state => state.customer.greeting)
    const applicationError = useSelector(state => state.customer.applicationError)
    const orderId = useSelector(state => state.customer.data.orderId)
    const caseId = useSelector(state => state.customer.data.caseId)
    const isAgentNoteOpen = useSelector(state => state.settings.isAgentNoteOpen)
    const embeddedShowNewCase = useSelector(state => state.embedded.embeddedShowNewCase)
    const args = useSelector(state => state.embedded.args)
    const alvariaUserId = useSelector(state => state.embedded.alvariaUserId)
    const dispatch = useDispatch()

    useEffect(() => {
        const init = async () => {
            if (!alvariaUserId) {
                await checkLogin(dispatch)
            }

            if (!appLoaded && alvariaUserId) {
                appLoaded = true
                dispatch(setEmbeddedPhone(true))
                const eventHandler = async (event) => {
                    const url = new URL(event.origin)
                    const parts = url.host.split('.')
                    parts.shift()
                    const origin = parts.join('.')
                    if ('via.aspect-cloud.net' === origin || event.origin === process.env.REACT_APP_EMBEDDED_BRIDGE_ORIGIN) {
                        const data = JSON.parse(event.data)
                        console.log('postMessage: ', event)
                        const state = store.getState() 
                        const salesSessions = state.salesSessions
                        switch (data.eventName) {
                            case 'SCREENPOP':
                                dispatch(handleNewCall(data))
                                break
                            case 'ACTIVE':
                                dispatch(handleActiveCall(data))
                                break
                             case 'CHAT_ENTITY_ADDED': 
                                if (salesSessions.enabled) {
                                    const callId = String(data?.payload?.callId ?? '')
                                    const raw    = String(data?.payload?.name ?? data?.payload?.entityID ?? '')
                                    const digits = raw.replace(/\D/g, '')        
                                    if (callId && digits) {
                                        dispatch(setChatDisplayNumberByCallId({ callId, name: digits }))
                                    }
                                }
                                break  
                            case 'NOT_READY':
                                dispatch(handleEndCallTask())
                                break
                            case 'LOGOUT':
                                dispatch(handleEndCallTask())
                                break
                            case 'CALL_CLEAR':
                                if (salesSessions.enabled) {
                                    dispatch(dismissChatByCallId(data.payload.callID))
                                } else {
                                    dispatch(handleCallClear())
                                }
                                break
                            case 'DISPOSITION':
                                dispatch(handleDisposition(data))
                                break
                            case 'ACCEPT_CALL_TIMEOUT':
                                if (salesSessions.enabled) {
                                    dispatch(dismissChatByCallId(data.payload.callID))
                                }
                            default:
                                break
                        }
                    }
                }

                window.addEventListener('message', eventHandler)

                const beforeUnloadHandler = (event) => {
                    // Recommended
                    event.preventDefault()

                    // Included for legacy support, e.g. Chrome/Edge < 119
                    event.returnValue = true
                }

                window.addEventListener('beforeunload', beforeUnloadHandler)

                dispatch(getStringTranslations())
            }
            installConsumerChat()
        }
        init()
    }, [alvariaUserId])


    function getComponent() {
        const onLogin = () => {
            dispatch(setShowLoginForm(false))
            dispatch(getProfileInfo())
        }

        if (showLoginForm) {
            return <LoginForm onLogin={onLogin}/>
        }


        if (existingCases.length > 0 || embeddedShowNewCase) {
            return <div>
                <div style={{marginTop: '10px'}} className="text-center">
                    {greeting ? <h1>{greeting}</h1> : null}
                </div>

                <CaseSelector args={args}/>
            </div>
        }

        let component
        if (applicationError) {
            component = <ErrorPage message={applicationError}/>
        } else if (orderId) {
            component = <Order/>
        } else if (caseId) {
            component = <CustomerForm/>
        } else {
            component = <HughesnetLogo/>
        }
        if (isAgentNoteOpen) {
            return <Row>
                <Col md={6} as="main" className="form-panel">{component}</Col>
                <Notes/>
            </Row>
        } else {
            return <Row as="main" className="form-panel">
                {component}
            </Row>
        }
    }


    const component = getComponent()

    return <>
        <div className="loading" style={{display: appLoadingState === 'pending' ? 'block' : ''}}>
            <div>
                <Row className="mt-3">
                    <Col md={{span: 2, offset: 5}}>
                        <div className="loading-container">
                            <BeatLoader color="#0092c5" size={40}/>
                        </div>
                    </Col>
                </Row>
            </div>
        </div>
        <WebPhone/>
        <Header/>
        <Container className="main-container" fluid>
            {component}
            <AlertPopup/>
        </Container>
    </>
}